import type { Entity as WorkbookEntity } from '@mathflat/domain/@entities/(Content)/Workbook/dto'
import { action, computed, makeObservable, observable, reaction } from 'mobx'
import type ErrorReportService from 'src/service/errorReport.service'

import { ERROR_REPORT_PROBLEM_PUBLIC_ANSWER_NOT_CONFIRMED } from '@/constant'
import {
  ERROR_REPORT_ARITHMETIC,
  ERROR_REPORT_CONCEPT,
  ERROR_REPORT_PROBLEM_CUSTOM,
  ERROR_REPORT_PROBLEM_PUBLIC,
} from '@/constant'

import { DTO } from '../DTO'

// 오류 신고
export class ErrorReport extends DTO {
  categoryId: number // 해당 문제에 대한 아이디
  private type?: 'ERROR_CATEGORY' | 'CONCEPT_DIVISION' | 'LEVEL_SUGGESTION' | 'DUPLICATE_PROBLEM' // 분류
  subtype?:
    | (typeof ERROR_REPORT_PROBLEM_PUBLIC)[number]['option'][number]['id']
    | (typeof ERROR_REPORT_PROBLEM_PUBLIC_ANSWER_NOT_CONFIRMED)[number]['option'][number]['id']
    | (typeof ERROR_REPORT_PROBLEM_CUSTOM)[number]['option'][number]['id']
    | (typeof ERROR_REPORT_CONCEPT)[number]['id'] // 소분류 - 문제 신고 선택칸
    | (typeof ERROR_REPORT_ARITHMETIC)[number]['id'] // 연산학습지

  content = '' // 직접 입력

  tagTop?: string | null
  originWorkbookId?: WorkbookEntity.Workbook['id'] // 시중교재 베이스로 만들었거나 시중교재일때 추가

  constructor({
    categoryId,
    type,
    tagTop,
    originWorkbookId,
  }: Pick<ErrorReport, 'categoryId' | 'tagTop' | 'originWorkbookId'> & {
    type: ErrorReportService['type']
  }) {
    super()
    this.categoryId = categoryId
    switch (type) {
      case 'ARITHMETIC':
        this.subtype = ERROR_REPORT_ARITHMETIC[0]['id']
        break
      case 'PROBLEM_PUBLIC':
        this.subtype = ERROR_REPORT_PROBLEM_PUBLIC[0]['option'][0]['id']
        break
      case 'PROBLEM_PUBLIC_ANSWER_NOT_CONFIRMED':
        this.subtype = ERROR_REPORT_PROBLEM_PUBLIC_ANSWER_NOT_CONFIRMED[0]['option'][0]['id']
        break
      case 'PROBLEM_CUSTOM':
        this.subtype = ERROR_REPORT_PROBLEM_CUSTOM[0]['option'][0]['id']
        break
      case 'CONCEPT':
      case 'LITTLE_CHAPTER':
        this.subtype = ERROR_REPORT_CONCEPT[0]['id']
        break
      default:
        this.subtype = ERROR_REPORT_ARITHMETIC[0]['id']
        break
    }
    this.tagTop = tagTop
    this.originWorkbookId = originWorkbookId
    this.getType()

    makeObservable(this, {
      categoryId: observable,
      subtype: observable,
      tagTop: observable,
      originWorkbookId: observable,
      content: observable,
      setSubType: action,
      bugReportData: computed,
    })

    reaction(
      () => this.subtype,
      () => {
        // 개념, 연산 외에는 문제 신고 - Constant 참고
        this.getType()
      },
    )
  }

  getType = () => {
    switch (this.subtype) {
      case 'ERROR_CATEGORY_PROBLEM':
      case 'ERROR_CATEGORY_ANSWER':
      case 'ERROR_CATEGORY_SOLUTION':
      case 'ERROR_CATEGORY_LAYOUT':
      case 'ERROR_CATEGORY_TYPO':
      case 'ERROR_CATEGORY_ETC':
      case 'ERROR_CATEGORY_CONTENT':
      case 'ERROR_CATEGORY_NONE_CONTENT':
      case 'ERROR_CATEGORY_NEED_ANSWER':
      case 'ERROR_CATEGORY_NONE_LAYOUT':
      case 'ERROR_CATEGORY_NEED_SHORTEN_CONTENT':
      case 'ERROR_CATEGORY_LACK_CONTENT':
      case 'ERROR_CATEGORY_MATCH':
        this.type = 'ERROR_CATEGORY'
        break
      case 'CONCEPT_DIVISION':
      case 'CONCEPT_DIVISION_PROBLEM':
        this.type = 'CONCEPT_DIVISION'
        break
      case 'LEVEL_SUGGESTION_LOWEST':
      case 'LEVEL_SUGGESTION_LOW':
      case 'LEVEL_SUGGESTION_MIDDLE':
      case 'LEVEL_SUGGESTION_HIGH':
      case 'LEVEL_SUGGESTION_HIGHEST':
        this.type = 'LEVEL_SUGGESTION'
        break
      case 'DUPLICATE_PROBLEM':
        this.type = 'DUPLICATE_PROBLEM'
        break

      default:
        this.type = 'ERROR_CATEGORY'
    }
  }

  get bugReportData() {
    return {
      categoryId: this.categoryId,
      type: this.type as req.BugReportData['type'],
      subtype: this.subtype as req.BugReportData['subtype'],
      content: this.content,
      additionalInfo: { originWorkbookId: this.originWorkbookId, tagTop: this.tagTop },
    }
  }

  setSubType = (subtype) => {
    this.subtype = subtype
  }
}
